import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import { HiEye, HiDownload, HiCheck } from 'react-icons/hi';
import * as FileSaver from "file-saver";
import { toast } from "react-toastify";
import ModalSkeleton from "../../../../components/Modals/ModalSkeleton";
import CustomButton from "../../../../components/common/Button/CustomButton";
import AvatarGroup from "../../../../components/common/Avatar/AvatarGroup";
import FileIcon from "components/common/FileIcon/FileIcon";
import SwitchWithIcon from "../../../../components/common/SwitchWithIcon";
import { apiPatchRequest } from "../../../../helpers/Requests";
import {
  getFileExtension,
  downloadFileFromServer,
  baseURLImage,
  sanitizeDocumentUrl,
  textTruncate,
  capitalizeFirstLetter,
  capitalizeAllLetters,
  capitalizeWords
} from "../../../../Utils/utils";
import useCareLabels from "hooks/useCareLabels";
import CareLabelSkeleton from "components/common/CareLabelSkeleton/CareLabelSkeleton";


const getStatusColorClass = (status) => {
  const statusLower = status?.toLowerCase();
  if (statusLower === 'completed' || statusLower === 'complete') {
    return 'bg-green-100 text-green-800 border border-green-200';
  }
  if (statusLower === 'pending') {
    return 'bg-yellow-100 text-yellow-800 border border-yellow-200';
  }
  if (statusLower === 'in_progress') {
    return 'bg-blue-100 text-blue-800 border border-blue-200';
  }
  return 'bg-gray-100 text-gray-800 border border-gray-200';
};

const ViewFeedbackModal = ({
  isOpen,
  closeModal,
  feedbackData,
  onStatusUpdate
}) => {
  const { participantLabelPlural, isLoading: isCareLabelsLoading } = useCareLabels();
  const [shouldTruncate, setShouldTruncate] = useState(true);
  const [showButton, setShowButton] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [isUpdatingCIP, setIsUpdatingCIP] = useState(false);
  const dropdownRef = useRef(null);

  const statusOptions = [
    { value: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800 border-blue-200' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200' }
  ];


  const getCIPValue = () => {
    return feedbackData?.cip ?? feedbackData?.continuous_improvement_plan ?? false;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowStatusDropdown(false);
      }
    };

    if (showStatusDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showStatusDropdown]);

  const handleReadMore = () => {
    setShouldTruncate(!shouldTruncate);
    setShowButton(false);
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!feedbackData?.uuid || !newStatus) {
      toast.error("Unable to update status. Please try again.");
      return;
    }

    setIsUpdatingStatus(true);
    setShowStatusDropdown(false);

    try {
      const updatePayload = {
        status: newStatus
      };

      const response = await apiPatchRequest(`/sp/feedback/update/${feedbackData.uuid}`, updatePayload);

      if (response?.status === 200) {
        toast.success("Status updated successfully!");
        if (onStatusUpdate) {
          onStatusUpdate(feedbackData.uuid, newStatus);
        }
      } else {
        throw new Error("Failed to update status");
      }
    } catch (error) {
    //   console.error(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCIPToggle = async () => {
    if (!feedbackData?.uuid) {
      toast.error("Unable to update Continuous Improvement Plan. Please try again.");
      return;
    }

    setIsUpdatingCIP(true);

    try {
      const currentCIPValue = getCIPValue();
      const newCIPValue = !currentCIPValue;
      const updatePayload = {
        continuous_improvement_plan: newCIPValue
      };

      const response = await apiPatchRequest(`/sp/feedback/update/${feedbackData.uuid}`, updatePayload);

      if (response?.status === 200) {
        toast.success(`Continuous Improvement Plan ${newCIPValue ? 'enabled' : 'disabled'} successfully!`);
        if (onStatusUpdate) {
          onStatusUpdate(feedbackData.uuid, feedbackData.status, newCIPValue);
        }
      } else {
        throw new Error("Failed to update Continuous Improvement Plan");
      }
    } catch (error) {
    //   console.error("CPI error",error);
    } finally {
      setIsUpdatingCIP(false);
    }
  };  const handleDownload = (file, name) => {
    if (file instanceof File) {
      const blob = new Blob([file], { type: file?.type });
      FileSaver.saveAs(blob, name || "downloadedFile");
    } else {
      downloadFileFromServer(
        file,
        name ? name : `download.${getFileExtension(file)}`
      );
    }
  };

  const renderAttachments = (items, indexKey) => {
    if (!items || items.length === 0) {
      return (
        <div className='text-sm text-gray-500'>
          No attachments available
        </div>
      );
    }

    return (
      <div
        key={indexKey}
        className='grid grid-cols-1 md:grid-cols-2 gap-x-2 gap-y-2 max-h-[100px] overflow-y-auto custom-scroll'
      >
        {items?.map((item, index) => {
          const fileName = item?.file_name || '';
          const fileUrl = item?.file_path || '';

          if (!fileUrl) return null;

          return (
            <div
              key={`${index}-${indexKey}`}
              className='border border-1 rounded-md px-2 py-1 text-sm'
            >
              <div className='flex items-center justify-start'>
                <div className='bg-blue-50 rounded-full h-8 w-8 flex items-center text-center justify-center'>
                  {FileIcon(getFileExtension(fileName.toLowerCase()), 12)}
                </div>
                <div className='flex-1 px-2'>
                  <span
                    style={{ fontWeight: "500" }}
                    className='font-medium truncate block'
                    title={fileName}
                  >
                    {textTruncate(fileName, 30, 20)}
                  </span>
                </div>
                <div className='ml-2 space-x-2 flex'>
                  <div
                    onClick={() => window.open(sanitizeDocumentUrl(baseURLImage(fileUrl)), "_blank")}
                    className='icon-eye-wrapper cursor-pointer'
                    title='View'
                  >
                    <HiEye className='icon-size' />
                  </div>
                  <div
                    onClick={() => handleDownload(sanitizeDocumentUrl(baseURLImage(fileUrl)), fileName)}
                    className='icon-download-wrapper cursor-pointer'
                    title='Download'
                  >
                    <HiDownload className='icon-size' />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const modalFooter = () => (
    <CustomButton
      clickHandler={closeModal}
      type='button'
      label='Close'
      variant='danger'
    />
  );

  if (!feedbackData) return null;

  return (
    <ModalSkeleton
      title='Feedback Information'
      cssClass='w-[50%]'
      isOpen={isOpen}
      closeModal={closeModal}
      modalFooter={modalFooter()}
    >
      <>
        <div className='mb-3 flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            {feedbackData.uuid && (
              <span className='inline-flex items-center px-2 py-2 rounded-md text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200'>
                ID: {feedbackData.uuid.substring(0, 8).toUpperCase()}
              </span>
            )}
            {feedbackData.status && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={isUpdatingStatus}
                  className={`inline-flex items-center px-2 py-2 rounded-md text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity ${getStatusColorClass(feedbackData.status)} ${
                    isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isUpdatingStatus ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    <>
                      {feedbackData.status?.toLowerCase() === 'in_progress'
                        ? 'In Progress'
                        : capitalizeAllLetters(feedbackData?.status)}
                      <svg className="ml-1 h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </>
                  )}
                </button>


                {showStatusDropdown && !isUpdatingStatus && (
                  <div className="absolute top-full left-0 mt-1 w-36 bg-white border border-gray-200 rounded-md shadow-lg z-50">
                    <div className="py-1">
                      {statusOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleStatusUpdate(option.value)}
                          disabled={option.value === feedbackData.status}
                          className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 flex items-center transition-colors ${
                            option.value === feedbackData.status
                              ? 'bg-gray-100 cursor-not-allowed opacity-50'
                              : 'cursor-pointer'
                          }`}
                        >
                          <span className={`inline-block w-2 h-2 rounded-full mr-2 ${option.color.split(' ')[0]}`}></span>
                          {option.label}
                          {option.value === feedbackData.status && (
                            <HiCheck className="ml-auto h-3 w-3 text-green-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className='flex items-center space-x-3'>
            <div className='text-right'>
              <p className='text-xs font-medium text-gray-900'>Continuous Improvement Plan</p>
            </div>
            <div className='flex items-center space-x-2'>
              {isUpdatingCIP && (
                <svg className="animate-spin h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              <SwitchWithIcon
                onChange={handleCIPToggle}
                checked={getCIPValue()}
                disabled={isUpdatingCIP}
                tooltip={`Continuous Improvement Plan: ${getCIPValue() ? 'Enabled' : 'Disabled'}`}
              />
            </div>
          </div>
        </div>

        <div>
          <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>
              {isCareLabelsLoading ? (
                <CareLabelSkeleton width="w-28" height="h-3.5" />
              ) : (
                participantLabelPlural
              )}
            </dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              <div className='flex items-center'>
                {feedbackData.participants && feedbackData.participants.length > 0 ? (
                  feedbackData.participants.length === 1 ? (
                    <span className='truncate whitespace-nowrap overflow-ellipsis block'>
                      {capitalizeWords(feedbackData?.participants[0]?.name) || 'N/A'}
                    </span>
                  ) : (
                    <AvatarGroup
                      arrayData={feedbackData.participants?.map(participant => ({
                        ...participant,
                        first_name: participant?.name?.split(' ')[0] || '',
                        last_name: participant?.name?.split(' ').slice(1).join(' ') || ''
                      }))}
                      cssClass='mt-0'
                      maxLength={10}
                      isNoRing={true}
                    />
                  )
                ) : (
                  <span className='text-gray-400'>
                    No{" "}
                    {isCareLabelsLoading ? (
                      <CareLabelSkeleton width="w-20" height="h-3.5" className="bg-gray-300 align-middle" />
                    ) : (
                      participantLabelPlural.toLowerCase()
                    )}
                  </span>
                )}
              </div>
            </dd>
          </div>
          <div className='bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Workers</dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              <div className='flex items-center'>
                {feedbackData.workers && feedbackData.workers.length > 0 ? (
                  feedbackData.workers.length === 1 ? (
                    <span className='truncate whitespace-nowrap overflow-ellipsis block'>
                      {feedbackData.workers[0]?.name || 'N/A'}
                    </span>
                  ) : (
                    <AvatarGroup
                      arrayData={feedbackData.workers?.map(worker => ({
                        ...worker,
                        first_name: worker?.name?.split(' ')[0] || '',
                        last_name: worker?.name?.split(' ').slice(1).join(' ') || ''
                      }))}
                      cssClass='mt-0'
                      maxLength={10}
                      isNoRing={true}
                    />
                  )
                ) : (
                  <span className='text-gray-400'>No workers</span>
                )}
              </div>
            </dd>
          </div>
          <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Date</dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {moment(feedbackData.date).format("DD/MM/YYYY")}
            </dd>
          </div>
             <div className='bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Feedback Type</dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {capitalizeFirstLetter(feedbackData?.feedback_assessment) || 'N/A'}
            </dd>
          </div>
          <div className='bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Subject</dt>
            <dd className='mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2'>
              {capitalizeFirstLetter(feedbackData?.subject) || 'N/A'}
            </dd>
          </div>
          <div className=' px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Details</dt>
            <div className='flex sm:col-span-2'>
              <span
                style={{ wordBreak: "break-word" }}
                className={`mt-1 text-sm text-gray-900 sm:mt-0 ${
                  shouldTruncate ? "truncate" : ""
                } first-letter:uppercase`}
                dangerouslySetInnerHTML={{
                  __html: shouldTruncate
                    ? capitalizeWords(textTruncate(feedbackData?.details, 40, 40))
                    : feedbackData.details,
                }}
              />
              {feedbackData.details?.length > 40 && showButton && (
                <p
                  className='text-blue-900 cursor-pointer whitespace-nowrap text-sm ml-2'
                  onClick={handleReadMore}
                >
                  Read More
                </p>
              )}
            </div>
          </div>
          <div className='bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6'>
            <dt className='text-sm font-medium text-gray-500'>Attachments</dt>
            <div className='sm:col-span-2'>
              {renderAttachments(feedbackData.attachments || [], 'attachments')}
            </div>
          </div>
        </div>
      </>
    </ModalSkeleton>
  );
};

export default ViewFeedbackModal;
